import { CAT_BREEDS, DOG_BREEDS } from '@snootsvet/shared/portal'

import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
} from '@/customer/components/ui/autocomplete'

/**
 * Species-aware breed suggestion input. Backed by the shared breed
 * lists, but the underlying `Autocomplete` (not `Combobox`) still
 * accepts arbitrary text - the list is a nudge toward canonical
 * spellings, not a whitelist. Any typed value flows through as-is.
 *
 * Two variants match the underlying `Input` / `InputGroup` split:
 *
 * - `box` (default): full poster-outline box, for standalone forms
 *   (register flow, add-pet).
 * - `underline`: bare bottom-rule, for use inside `EditableField`
 *   rows on the pet edit page.
 */
export function BreedAutocomplete({
  species,
  value,
  onValueChange,
  variant,
  autoFocus,
  placeholder = 'e.g. Golden Retriever',
  disabled,
  'aria-invalid': ariaInvalid,
}: {
  /**
   * Determines which breed list to suggest. Pass `null` while the
   * species is unknown; callers should also set `disabled` so the
   * input reflects the not-ready state.
   */
  species: string | null | undefined
  value: string
  onValueChange: (value: string) => void
  variant?: 'box' | 'underline'
  autoFocus?: boolean
  placeholder?: string
  disabled?: boolean
  'aria-invalid'?: boolean
}) {
  const suggestions = species === 'dog' ? DOG_BREEDS : species === 'cat' ? CAT_BREEDS : []

  return (
    <Autocomplete value={value} onValueChange={onValueChange} items={suggestions}>
      <AutocompleteInput
        autoFocus={autoFocus}
        variant={variant}
        placeholder={placeholder}
        autoComplete="off"
        showTrigger={false}
        disabled={disabled}
        aria-invalid={ariaInvalid}
      />
      <AutocompleteContent>
        {/* Render-fn child on List receives base-ui's already-filtered
            items, so typing narrows the results. Base UI implicitly
            wraps the fn in a Collection for flat lists. The popup
            hides itself (via `data-empty:hidden` on the Positioner)
            once the filter narrows to zero matches, so unusual
            breeds don't have to see a "no results" message. */}
        <AutocompleteList>
          {(item: string) => (
            <AutocompleteItem key={item} value={item}>
              {item}
            </AutocompleteItem>
          )}
        </AutocompleteList>
      </AutocompleteContent>
    </Autocomplete>
  )
}
